import { SEO } from "../components/SEO";

export function Privacy() {
  return (
    <>
      <SEO
        title="Privacy Policy | StarSinger MCP"
        description="Privacy Policy for the StarSinger MCP API. Learn how we collect, use, and protect your information."
        pathname="/privacy"
      />
      <div className="max-w-3xl mx-auto px-6 py-20">
      <h1 className="text-3xl font-bold text-white mb-2">
        StarSinger MCP API — Privacy Policy
      </h1>
      <p className="text-white/40 text-sm mb-10">Last updated: March 30, 2026</p>

      <div className="space-y-8 text-white/70 leading-relaxed text-[15px]">
        <section>
          <h2 className="text-xl font-semibold text-white mb-3">1. Introduction</h2>
          <p>
            This Privacy Policy describes how Veronata, Inc. ("we", "us", "our")
            collects, uses, and protects information when you use the StarSinger
            MCP API ("API") and the developer portal at mcp.starsinger.ai.
          </p>
          <p className="mt-3">
            This policy is specific to the MCP API service. For the main
            StarSinger.ai app, see the{" "}
            <a
              href="https://starsinger.ai/privacy"
              className="text-amber-400 hover:text-amber-300"
              target="_blank"
              rel="noopener noreferrer"
            >
              StarSinger.ai Privacy Policy
            </a>
            .
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">
            2. Information We Collect
          </h2>

          <h3 className="text-lg font-semibold text-white mt-4 mb-2">
            Account Information
          </h3>
          <ul className="list-disc pl-5 space-y-1">
            <li>Email address (for account creation and communication)</li>
            <li>Name (if provided via Google or Apple sign-in)</li>
            <li>Authentication provider (Google, Apple)</li>
          </ul>

          <h3 className="text-lg font-semibold text-white mt-4 mb-2">
            API Usage Data
          </h3>
          <ul className="list-disc pl-5 space-y-1">
            <li>API key identifiers (not the key itself in logs)</li>
            <li>Endpoints called and request parameters</li>
            <li>Request timestamps and response times</li>
            <li>Daily request counts per API key</li>
            <li>HTTP status codes</li>
          </ul>

          <h3 className="text-lg font-semibold text-white mt-4 mb-2">
            Technical Data
          </h3>
          <ul className="list-disc pl-5 space-y-1">
            <li>IP address (when available via request headers)</li>
            <li>User-Agent string (identifies MCP client — Claude, Cursor, etc.)</li>
          </ul>

          <h3 className="text-lg font-semibold text-white mt-4 mb-2">
            What We Do NOT Collect
          </h3>
          <ul className="list-disc pl-5 space-y-1">
            <li>No cookies are set by the API endpoints</li>
            <li>No browser fingerprinting</li>
            <li>No tracking pixels</li>
            <li>No content of your AI agent conversations</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">
            3. How We Use Your Information
          </h2>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong className="text-white">Rate limiting:</strong> Daily
              request counts enforce tier-based usage limits.
            </li>
            <li>
              <strong className="text-white">Abuse prevention:</strong> IP
              addresses and usage patterns help detect scraping, brute-force
              attacks, and Terms violations.
            </li>
            <li>
              <strong className="text-white">Service improvement:</strong>{" "}
              Aggregate usage analytics (most popular endpoints, search queries,
              error rates) help us improve the API.
            </li>
            <li>
              <strong className="text-white">Billing:</strong> For paid plans,
              usage data informs billing and tier recommendations.
            </li>
            <li>
              <strong className="text-white">Communication:</strong> Email may
              be used for critical service updates, security notices, or
              Terms changes. We do not send marketing emails without consent.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">
            4. Data Storage and Security
          </h2>
          <p>
            Data is stored on Convex (backend) and processed in the United
            States. API keys are stored as hashed values. We implement
            industry-standard security measures including encryption in transit
            (TLS) and access controls.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">
            5. Data Retention
          </h2>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong className="text-white">Account data:</strong> Retained
              while your account is active. Deleted upon request.
            </li>
            <li>
              <strong className="text-white">API usage logs:</strong> Retained
              for 90 days for operational purposes, then aggregated or deleted.
            </li>
            <li>
              <strong className="text-white">Daily usage counters:</strong>{" "}
              Retained for 30 days for rate limiting, then deleted.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">
            6. Third-Party Services
          </h2>
          <p>We use the following third-party services:</p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>
              <strong className="text-white">Convex:</strong> Backend
              infrastructure and database
            </li>
            <li>
              <strong className="text-white">Stripe:</strong> Payment processing
              for paid plans
            </li>
            <li>
              <strong className="text-white">Vercel:</strong> Portal hosting
            </li>
          </ul>
          <p className="mt-2">
            Each provider has their own privacy policy and processes data
            according to their terms.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">7. Your Rights</h2>
          <p>You may:</p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>Request a copy of the data we hold about you</li>
            <li>Request deletion of your account and associated data</li>
            <li>Revoke API keys at any time via your dashboard</li>
            <li>Opt out of non-essential communications</li>
          </ul>
          <p className="mt-2">
            To exercise these rights, contact{" "}
            <a
              href="mailto:privacy@starsinger.ai"
              className="text-amber-400 hover:text-amber-300"
            >
              privacy@starsinger.ai
            </a>
            .
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">
            8. Children's Privacy
          </h2>
          <p>
            The API is not directed at children under 13. We do not knowingly
            collect information from children under 13.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">9. Changes</h2>
          <p>
            We may update this Privacy Policy from time to time. Changes will be
            posted on this page with an updated "Last updated" date.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">10. Contact</h2>
          <p>
            Questions about this Privacy Policy? Contact{" "}
            <a
              href="mailto:privacy@starsinger.ai"
              className="text-amber-400 hover:text-amber-300"
            >
              privacy@starsinger.ai
            </a>
          </p>
        </section>
      </div>
      </div>
    </>
  );
}
